"use client";

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

type RevealProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  y?: number;
  scale?: number;
  blur?: number;
};

export default function Reveal({
  children,
  className,
  delay = 0,
  duration = 0.6,
  y = 0,
  scale = 1,
  blur = 0,
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setShown(true);
      setDone(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-100px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shown || done) return;
    const timer = setTimeout(() => setDone(true), (delay + duration) * 1000 + 150);
    return () => clearTimeout(timer);
  }, [shown, done, delay, duration]);

  const style: CSSProperties | undefined = done
    ? undefined
    : {
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : `translateY(${y}px) scale(${scale})`,
        filter: blur > 0 ? (shown ? "blur(0px)" : `blur(${blur}px)`) : undefined,
        transition: `opacity ${duration}s ease-out ${delay}s, transform ${duration}s ease-out ${delay}s, filter ${duration}s ease-out ${delay}s`,
      };

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
}
